import { KeyboardArrowDown } from "@mui/icons-material"
import { CircularProgress, Grid, Input, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material"
import { waitForTransactionReceipt } from "@wagmi/core"
import BigNumber from "bignumber.js"
import React, { useEffect } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { useAccount, useBalance, useConnect, useWaitForTransactionReceipt, useWriteContract } from "wagmi"
import { injected } from "wagmi/connectors"
import { UNKNOWNIcon } from "../../assets"
import { faucetABI, faucetContractAddresses } from "../../contracts"
import { ERC20Abi } from "../../contracts/ERC20"
import { poolContract, poolContractAddresses } from "../../contracts/pool"
import useGlobalStore from "../../stores/global"
import { Button, DECIMALS, MAX_SPENDING_LIMIT, NeoXChain, StyledPaper, StyledSnackbar, config, tokenMapping } from "../commons"
import { CustomSnackbarContent } from "../commons/StyledSnackbar"
import { displayNumber } from "../commons/utils"

const InputForm = () => {
  const { connectAsync } = useConnect();
  const { address, isConnected } = useAccount();
  const [open, setOpen] = React.useState(false);
  const [snackbarMsg, setSnackbarMsg] = React.useState<CustomSnackbarContent>({ title: '', type: 'success' })
  const [formInput, setFormInput] = React.useState<string>('0')
  const [searchParams, setSearchParams] = useSearchParams();
  const formType = searchParams.get('type') ?? 'deposit';

  const tokenDetailsPageHistoryCount = useGlobalStore((state) => state.tokenDetailsPageHistoryCount)
  const setTokenDetailsPageHistoryCount = useGlobalStore((state) => state.setTokenDetailsPageHistoryCount)
  const getTokenAllowance = useGlobalStore((state) => state.getTokenAllowance)

  const params = useParams()
  const asset = params?.denom ?? ''
  const reseedStore = useGlobalStore((state) => state.seedStore)
  const markets = useGlobalStore((state) => state.markets)
  const userAccountData = useGlobalStore((state) => state.userAccountData)
  const market = markets.find((market) => market.asset.toUpperCase() === asset.toUpperCase())
  const assetSymbol = market?.asset ?? asset
  const userDataPerMarket = useGlobalStore((state) => state.userDataPerMarket)
  const userData = userDataPerMarket.find((market) => market.asset.toUpperCase() === asset.toUpperCase())
  const tokenInfo = tokenMapping[asset.toUpperCase()]
  const TokenIcon = tokenInfo?.iconUrl ?? UNKNOWNIcon
  const formattedFormType = formType.charAt(0).toUpperCase() + formType.slice(1)

  //prep contract addresses
  const network = useGlobalStore((state) => state.network)
  const poolContractAddress = poolContractAddresses[network]
  const faucetContractAddress = faucetContractAddresses[network]

  //get user balance
  const { data: userBalance } = useBalance({
    address: address,
    token: market?.contractAddress,
    query: {
      refetchInterval: 3_000
    }
  })

  const userAssetBalance = new BigNumber(userBalance?.value?.toString() ?? 0).shift(-(market?.decimals ?? DECIMALS))
  const maxBorrowAmount = Math.min(new BigNumber(userAccountData?.availableBorrowsBase ?? 0)
    .div(new BigNumber(market?.price ?? 0))
    .mul(1).toNumber(), (market!.totalSupplied - market!.totalBorrowed))

  const maxRepay =
    Math.min(new BigNumber(userAccountData?.totalDebtBase ?? 0)
      .div(new BigNumber(market?.price ?? 0))
      .mul(1).toNumber(), Number(userData?.scaledVariableDebt ?? 0),
      new BigNumber(userAssetBalance).toNumber())
  const maxWithdraw = Math.min(
    new BigNumber(userAccountData?.totalCollateralBase ?? 0)
      .minus(new BigNumber(userAccountData?.totalDebtBase ?? 0).div(new BigNumber(userAccountData?.currentLiquidationThreshold ?? 0).div(100)))
      .mul(1).div(new BigNumber(market?.price ?? 0)).toNumber(), //the amount available to withdraw based on liquidation threshold
    Number(userData?.currentScaledTokenBalance ?? 0),  //amount of underlying token equivalent deposited
    (market!.totalSupplied - market!.totalBorrowed)) //account for available liquidity of the market

  const { writeContractAsync, isPending, data } = useWriteContract({
    mutation: {
      // onSettled: (() => {
      // }),
      onError: ((error: Error) => {
        setOpen(true)
        setSnackbarMsg({
          type: 'error',
          title: error?.name as string ?? 'An error occurred',
          subtitle: error?.message as string ?? 'An error occurred'
        })
        // setError(error)
      }),
      onSuccess: (() => {
        // setTxHash(data as string)
      })
    }
  })

  const { isLoading, isSuccess } = useWaitForTransactionReceipt({
    pollingInterval: 1_000,
    hash: data,
  })

  //Loading State
  const mintLoading = isPending || isLoading
  useEffect(() => {
    if (!data || isLoading || !isSuccess) return
    setOpen(true)
    setSnackbarMsg(
      {
        type: 'success',
        title: "Successful!",
        cta: {
          type: 'link',
          text: `${data}`
        }
      }
    )
    setFormInput('0')
    //  TX: <Link style={{ color: customPaletteColors.brand.primary }} 
    // target='_blank' to={`https://xt4scan.ngd.network/tx/${data}`}>
    //   {data?.slice(0, 16) + "..."}
    //   </Link>
    reseedStore()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  const handleMaxBtn = () => {
    switch (formType) {
      case 'deposit':
        setFormInput(userAssetBalance.toString())
        break;
      case 'withdraw':
        setFormInput(maxWithdraw.toString())
        break;
      case 'borrow':
        //TODO: find borrowable amount
        setFormInput(maxBorrowAmount.toString())
        break;
      case 'repay':
        setFormInput(maxRepay.toString())
        break;
    }
  }

  const mintToken = async (token: `0x${string}`, address: `0x${string}` | undefined, amount: string) => {
    try {
      if (!isConnected || !address) await connectAsync({ chainId: NeoXChain.id, connector: injected() })
      await writeContractAsync({
        address: faucetContractAddress,
        abi: faucetABI,
        functionName: "mint",
        args: [
          token, //usdc
          address,
          amount
        ]
      })
    }
    catch (e) { console.log(e) }
  }

  const submitForm = async (assetAddress: `0x${string}`, amount: number) => {
    try {
      if (!isConnected) await connectAsync({ chainId: NeoXChain.id, connector: injected() })
      let fnName
      let args: any
      switch (formType) {
        case 'deposit':
          fnName = 'supply' as "supply"
          args = [
            assetAddress,
            BigInt(new BigNumber(amount.toString()).shift(market?.decimals ?? DECIMALS).toFixed(0)),
            address,
            0
          ]
          break;
        case 'withdraw':
          const isMaxWithdrawal = false
          fnName = 'withdraw' as "withdraw"
          args = [
            assetAddress,
            isMaxWithdrawal ? MAX_SPENDING_LIMIT : BigInt(new BigNumber(amount.toString()).shift(market?.decimals ?? DECIMALS).toFixed(0)),
            address
          ]
          break;
        case 'borrow':
          fnName = 'borrow' as "borrow"
          args = [
            assetAddress,
            BigInt(new BigNumber(amount.toString()).shift(market?.decimals ?? DECIMALS).toFixed(0)),
            2, //1: stable, 2: variable
            0, //0 means no referral
            address,
          ]
          break;
        case 'repay':
          const isMaxRepay = amount === maxRepay
          fnName = 'repay' as "repay"
          args = [
            assetAddress,
            isMaxRepay ? MAX_SPENDING_LIMIT : BigInt(new BigNumber(amount.toString()).shift(market?.decimals ?? DECIMALS).toFixed(0)),
            2, //1: stable, 2: variable
            address
          ]
          break;
      }
      if (!fnName || !address || !args) return

      //Check if token is approved
      const allowance = await getTokenAllowance(assetAddress, address, poolContractAddress)
      if (allowance < amount) {
        const approveHash: `0x${string}` = await writeContractAsync({
          address: assetAddress,
          abi: ERC20Abi,
          functionName: 'approve',
          args: [
            poolContractAddress,
            BigInt(MAX_SPENDING_LIMIT),
            // BigInt(new BigNumber(amount.toString()).shift(DECIMALS).toString())
          ]
        })
        await waitForTransactionReceipt(config, {
          pollingInterval: 1_000,
          hash: approveHash,
        })
      }

      await writeContractAsync({
        address: poolContractAddress,
        abi: poolContract.abi,
        functionName: fnName,
        args: args
      })
    }
    catch (e) { console.log(e) }
  }

  const handleChange = (event: SelectChangeEvent) => {
    // setFormType(event.target.value as string);
    setSearchParams({ type: event.target.value });
    setTokenDetailsPageHistoryCount(tokenDetailsPageHistoryCount - 1)
    setFormInput('0')
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormInput(event.target.value.replace("-", ""));
  }

  //validation
  let selectInput = 'deposit' as 'deposit' | 'borrow' | 'withdraw' | 'repay'
  switch (searchParams.get('type')) {
    case 'deposit':
    case 'borrow':
    case 'withdraw':
    case 'repay':
      selectInput = searchParams.get('type') as 'deposit' | 'borrow' | 'withdraw' | 'repay'
      break;
    default:
      selectInput = 'deposit'
      break;
  }

  let amountTextBasedOnType
  switch (formType) {
    case 'deposit':
      amountTextBasedOnType = `Wallet: ${displayNumber(userAssetBalance.toNumber(), 6, 6)}`
      break;
    case 'withdraw':
      amountTextBasedOnType = `Available: ${displayNumber(maxWithdraw, 6, 6)}`
      break;
    case 'borrow':
      amountTextBasedOnType = `Available: ${displayNumber(maxBorrowAmount, 6, 6)}` //change to avaliable borrow
      break;
    case 'repay':
      amountTextBasedOnType = `Repay: ${displayNumber(maxRepay, 6, 6)}`
  }

  //validation
  if (!market) {
    return null
  } //userData not found; TODO: add a not found page

  return (
    <Grid container item>
      <StyledPaper color='primary' sx={{ padding: '1.5rem 2rem', width: '100%' }}>
        <StyledSnackbar
          open={open}
          autoHideDuration={10000}
          setOpen={setOpen}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          customContent={snackbarMsg ?? ''}
        />
        <Grid container flexDirection={'column'} gap={3.5}>
          <Grid item>
            <Select
              id="token-form-select"
              value={selectInput}
              defaultValue="deposit"
              onChange={handleChange}
              IconComponent={KeyboardArrowDown}
              sx={{
                color: 'text.primary',
                backgroundColor: 'background.translucent',
                borderRadius: '8px',
                padding: '0rem 0.5rem',
                border: `1px solid`,
                borderColor: 'background.border',
              }}
            >
              <MenuItem value={'deposit'}>
                <Typography variant="r">
                  Deposit
                </Typography>
              </MenuItem>
              <MenuItem value={'withdraw'}>
                <Typography variant="r">
                  Withdraw
                </Typography>
              </MenuItem>
              <MenuItem value={'borrow'}>
                <Typography variant="r">
                  Borrow
                </Typography>
              </MenuItem>
              <MenuItem value={'repay'}>
                <Typography variant="r">
                  Repay
                </Typography>
              </MenuItem>
            </Select>
            {/* <Button variant='contained' color='secondary' translucent sx={{ backgroundColor: 'background.translucent' }}>
              <Typography variant="r">
                Deposit
              </Typography>
            </Button> */}
          </Grid>
          <Grid id='asset-form-input' item>
            <Typography variant='s' color='text.secondary'>
              You {formattedFormType}
            </Typography>
            <Grid item container flexWrap={'nowrap'} justifyContent={'center'} alignItems={'center'}>
              <Input
                type="number"
                onInput={handleInputChange}
                value={formInput.toString()}
                inputProps={{
                  min: 0
                }}
                placeholder='0.00' fullWidth sx={{
                  backgroundColor: 'background.translucent',
                  borderRadius: '8px 0px 0px 8px',
                  border: `1px solid`,
                  borderColor: 'background.border',
                  padding: '0.2rem 0.5rem',
                  margin: '0.3rem 0rem',
                }} />
              <Grid item sx={{ height: '100%' }}>
                <StyledPaper color='primary' sx={{ padding: { xs: '0.64rem 0.6rem', sm: '0.54rem 0.6rem' }, height: '100%', borderRadius: '0px 8px 8px 0px' }}>
                  <Grid container gap={0.5} alignItems={'center'} flexWrap={'nowrap'}>
                    <TokenIcon style={{ height: '1.3rem' }} />
                    <Typography variant='s' color='text.primary'>
                      {assetSymbol}
                    </Typography>
                  </Grid>
                </StyledPaper>
              </Grid>
            </Grid>
            <Grid container alignItems={'center'}
              justifyContent={'space-between'} gap={1} flexWrap={'nowrap'}>
              <Typography variant='xs' color='text.secondary'>
                ${displayNumber(Number(formInput) * (market?.price ?? 0), 2, 2)}
              </Typography>
              <Grid item container justifyContent={'flex-end'} flexWrap={'nowrap'} gap={1.2} sx={{ placeItems: 'center' }}>
                <Typography variant='xs' color='text.secondary'>
                  {amountTextBasedOnType}
                </Typography>
                <Button color='secondary' sx={{ padding: '0.1rem 0rem', minWidth: '3rem' }} onClick={() => handleMaxBtn()}>
                  <Typography variant='xs' color='text.secondary'>
                    Max
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid>
            <Button disabled={mintLoading} variant='contained' color='primary' sx={{ width: '100%' }}
              onClick={() => submitForm(market.contractAddress, Number(formInput))}>
              <Typography variant="r">
                {mintLoading ? <CircularProgress size={20} color='primary' sx={{ marginTop: '0.4rem' }} /> : formattedFormType}
              </Typography>
            </Button>
            <Button disabled={mintLoading} color="secondary" variant='contained' sx={{ width: '100%', marginTop: '1rem' }} onClick={() =>
              mintToken(market.contractAddress, address, new BigNumber("100").shift(market?.decimals ?? DECIMALS).toString())}
            >
              <Typography variant="r">
                {mintLoading ? <CircularProgress size={20} color='primary' sx={{ marginTop: '0.4rem' }} /> : 'Mint'}
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </StyledPaper>
    </Grid>
  )
}
export default InputForm